import React from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  ListSubheader,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import {
  ASSIGNEE_KIND_AI,
  assigneeOptionLabel,
  isSameAssigneeOption,
} from '../../lib/ticketAssignees';

const GROUP_TEAM = 'Team members';
const GROUP_AI = 'AI Workers';

/**
 * Grouped assignee picker — humans and active AI Studio agents.
 * @param {{
 *   options: object[],
 *   value: object | null,
 *   onChange: (value: object | null) => void,
 *   disabled?: boolean,
 *   helperText?: string,
 *   role?: string,
 * }} props
 */
export default function TicketAssigneeAutocomplete({
  options,
  value,
  onChange,
  disabled = false,
  helperText,
  role,
}) {
  const defaultHelper =
    role === 'company_admin'
      ? 'Anyone in your company, AI workers from AI Studio, or escalate to a Super Admin.'
      : 'Someone in your company or an AI worker from AI Studio. Only a company admin can assign to a Super Admin.';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Autocomplete
        options={options}
        groupBy={(option) =>
          option.kind === ASSIGNEE_KIND_AI ? GROUP_AI : GROUP_TEAM
        }
        getOptionLabel={assigneeOptionLabel}
        isOptionEqualToValue={isSameAssigneeOption}
        value={value}
        onChange={(_, v) => onChange(v)}
        disabled={disabled}
        slotProps={{
          popper: { sx: { zIndex: 1500 } },
        }}
      renderGroup={(params) => (
        <li key={params.key}>
          <ListSubheader
            component="div"
            sx={{
              fontWeight: 700,
              fontSize: '0.75rem',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              lineHeight: 2.5,
              bgcolor: 'background.paper',
              color: 'text.secondary',
            }}
          >
            {params.group}
          </ListSubheader>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const isAi = option.kind === ASSIGNEE_KIND_AI;
        return (
          <Box component="li" key={key} {...rest}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ width: '100%', py: 0.25 }}
            >
              {isAi ? (
                <SmartToyOutlinedIcon fontSize="small" color="secondary" />
              ) : (
                <Box sx={{ width: 20 }} />
              )}
              <Typography variant="body2" sx={{ flex: 1 }}>
                {assigneeOptionLabel(option)}
              </Typography>
              {isAi && (
                <Chip
                  label="AI Worker"
                  size="small"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              )}
            </Stack>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Assignee"
          placeholder="Unassigned"
          size="small"
          helperText={helperText ?? defaultHelper}
          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
          sx={{
            '& .MuiInputBase-root': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        />
      )}
    />
    </Box>
  );
}
